import { FieldArray } from 'formik';
import { FormikProvider } from 'formik';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { queryCache } from 'react-query';
import { Poppins, Spacer, TableSkeleton } from 'src/common';
import { Scope, SortState, VariableEdit } from './types';
import _ from 'lodash';
import {
  categorizeVariables,
  checkIsDirty,
  genEmptyVariable,
  sortVariables,
  transformVariablesToGqlInput,
  validationSchema,
} from './util';
import { useFormik } from 'formik';
import useScenarioNames from './useMappedNames';
import CheckBox from 'src/components/form/CheckBox';
import { motion } from 'framer-motion';
import { Head, Row } from './comps';
import Button from 'src/components/form/Button';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { Div } from './styled';
import useAssessmentVariables from './useAssessmentVariables';
import { useAuth } from 'src/state/auth';
import { useCache } from 'src/graphql';
import VariableCSVReader from 'src/components/VariableCSVReader';
import { CsvVariable } from 'src/components/VariableCSVReader/types';
import { useUpsertAssessmentVariablesMutation, useUpsertWorkspaceVariablesMutation } from 'src/graphql/_generated';

interface WorkspaceVariablesFormProps {
  onClose: () => void;
  scope: Scope;
  onDirtyChange?: (isDirty: boolean) => void;
  readOnly?: boolean;
  isModal?: boolean;
  hideEmptyVariablesControlled?: boolean;
}

const WorkspaceVariablesForm: React.FC<WorkspaceVariablesFormProps> = ({
  onClose,
  scope,
  onDirtyChange,
  isModal,
  hideEmptyVariablesControlled = true,
}) => {
  const [initialVariables, setInitialVariables] = useState<VariableEdit[]>([]);
  const [preCSVVariables, setPreCSVVariables] = useState<VariableEdit[]>([]);
  const [error, setError] = useState('');
  const [variablesRepo, setVariablesRepo] = useState<VariableEdit[]>([]);
  const [showAssessmentVarOnly, setShowAssessmentVarOnly] = useState(false);
  const [hideEmptyVariables, setHideEmptyVariables] = useState(hideEmptyVariablesControlled);
  const [sort, setSort] = useState<SortState>({ by: 'name', isAsc: true });
  const { variables: wVars, profile } = useAuth();
  const { assessmentVariables, isAssessmentVariablesLoading } = useAssessmentVariables({
    assessmentId: scope.type === 'assessment' ? scope.id : undefined,
  });

  const { scenarioNames, assessmentNames } = useScenarioNames({
    variables: variablesRepo,
  });
  const { refetchQuery } = useCache();

  const [upsertWorkspaceVariables] = useUpsertWorkspaceVariablesMutation();
  const [upsertAssessmentVariables] = useUpsertAssessmentVariablesMutation();

  useEffect(() => {
    setHideEmptyVariables(hideEmptyVariablesControlled);
  }, [hideEmptyVariablesControlled]);

  useLayoutEffect(() => {
    if (!isAssessmentVariablesLoading) {
      const assessmentVariablesInit = assessmentVariables || [];
      const repo = [...wVars, ...assessmentVariablesInit];

      setVariablesRepo(repo);

      let vars: VariableEdit[];
      if (scope.type === 'workspace') {
        vars = wVars;
      } else {
        const wVarIds = new Set(wVars.map((wv) => wv.id));
        const cleanAssessmentVars = assessmentVariablesInit.filter((av) => !wVarIds.has(av.id));
        const assessmentVarNames = cleanAssessmentVars.map((v) => v.name);
        const wVarsWithoutOverrides = wVars.filter((wv) => !assessmentVarNames.includes(wv.name));
        vars = [...wVarsWithoutOverrides, ...cleanAssessmentVars];
      }

      if (vars.length) {
        setInitialVariables(vars);
      } else {
        setInitialVariables([genEmptyVariable(scope.type)]);
      }
    }
  }, [isAssessmentVariablesLoading, scope, showAssessmentVarOnly, hideEmptyVariables, wVars, assessmentVariables]);

  const formik = useFormik({
    initialValues: {
      variables: initialVariables,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const promises: Promise<any>[] = [];

      if (!showAssessmentVarOnly) {
        const isWorkspaceScope = scope.type === 'workspace';
        const excludeDeleteNames = isWorkspaceScope
          ? []
          : values.variables.filter((el) => !el.workspace_variable && !el.isGlobal).map((el) => el.name);

        const wvInput = isWorkspaceScope
          ? values.variables
          : values.variables.filter((el) => el.workspace_variable || el.isGlobal);
        const wvOriginal = isWorkspaceScope
          ? initialVariables
          : initialVariables.filter((el) => el.workspace_variable || el.isGlobal);
        const categorized = categorizeVariables(wvInput, wvOriginal, excludeDeleteNames, 'workspace');

        if (categorized.length) {
          promises.push(
            upsertWorkspaceVariables({
              variables: {
                workspaceId: profile?.activeWorkspace?.id!,
                variables: transformVariablesToGqlInput(categorized),
              },
            }),
          );
        }
      }

      if (scope.type === 'assessment') {
        const assessmentInput = values.variables.filter((el) => {
          if (el._isReverted) return false;
          if (el.workspace_variable || el.isGlobal) return false;

          const existsInAssessmentVars = assessmentVariables?.some((av) => av.id === el.id);
          const isNewOverride = !!el._isNewOverwrite;
          const isNewPureAssessmentVar = !!el._isNew && !wVars?.some((wv) => wv.name === el.name);
          return existsInAssessmentVars || isNewOverride || isNewPureAssessmentVar;
        });
        const assessmentOriginal = initialVariables.filter((el) => {
          if (el.workspace_variable || el.isGlobal) return false;
          return assessmentVariables?.some((av) => av.id === el.id) || el._isNewOverwrite || el._isNew;
        });
        const categorized = categorizeVariables(assessmentInput, assessmentOriginal, [], 'assessment');

        if (categorized.length) {
          promises.push(
            upsertAssessmentVariables({
              variables: {
                assessmentId: scope.id,
                variables: transformVariablesToGqlInput(categorized),
              },
            }),
          );
        }
      }

      return Promise.all(promises)
        .then(async () => {
          queryCache.invalidateQueries();
          await refetchQuery({ include: ['profile'] });
          setPreCSVVariables([]);
          onClose();
        })
        .catch((err: Error) => setError(err.message || 'Something went wrong'));
    },
  });

  const { values, handleSubmit, isValid, isSubmitting, setFieldValue, resetForm } = formik;

  const initialVariablesRef = useRef<string>('');
  useEffect(() => {
    const serialized = JSON.stringify(initialVariables.map((v) => ({ id: v.id, name: v.name })));
    if (initialVariablesRef.current && initialVariablesRef.current !== serialized) {
      resetForm({ values: { variables: initialVariables } });
    }
    initialVariablesRef.current = serialized;
  }, [initialVariables, resetForm]);

  useEffect(() => {
    const sorted = sortVariables(values.variables, sort);
    if (sorted) {
      setFieldValue('variables', sorted);
    }
  }, [sort]);

  const onOverride = (name: string, override: boolean) => {
    const original = wVars.find((el) => el.name === name);
    if (!override) {
      const newVariables = _.cloneDeep(values.variables);
      const idx = newVariables.findIndex((el) => el.name === name);

      if (original && idx !== -1) {
        newVariables[idx] = { ..._.cloneDeep(original), _isReverted: true };
        setFieldValue(`variables`, newVariables);
      }
    } else {
      const newVariables = _.cloneDeep(values.variables);
      const idx = newVariables.findIndex((el) => el.name === name);

      if (original && idx !== -1) {
        const targetVariable = _.cloneDeep(original);
        newVariables[idx] = {
          ...targetVariable,
          workspace_variable: false,
          isGlobal: false,
          isInUse: [],
          _isNewOverwrite: true,
        };

        setFieldValue(`variables`, newVariables);
      }
    }
  };

  const handleCSVApply = (csvVariables: CsvVariable[]) => {
    if (!csvVariables.length) return;

    setPreCSVVariables(_.cloneDeep(values.variables));

    const newVariables = _.cloneDeep(values.variables);

    csvVariables.forEach((csvVar) => {
      const existingVarIndex = newVariables.findIndex((v) => v.name === csvVar.name);

      const existsInWorkspaceVars = wVars?.some((wv) => wv.name === csvVar.name);

      if (existingVarIndex !== -1) {
        const existingVar = newVariables[existingVarIndex];

        if (scope.type === 'assessment' && existsInWorkspaceVars) {
          newVariables[existingVarIndex] = {
            ...existingVar,
            value: csvVar.value || 0,
            currency: csvVar.currency,
            workspace_variable: false,
            isGlobal: false,
            isInUse: [],
            _isNewOverwrite: true,
            _isFromCSV: true,
          };
        } else {
          newVariables[existingVarIndex] = {
            ...existingVar,
            value: csvVar.value || 0,
            currency: csvVar.currency,
          };
        }
      } else if (scope.type === 'assessment' && existsInWorkspaceVars) {
        const workspaceVar = wVars?.find((wv) => wv.name === csvVar.name);
        if (workspaceVar) {
          const newVar: VariableEdit = {
            ...workspaceVar,
            value: csvVar.value || 0,
            currency: csvVar.currency,
            workspace_variable: false,
            isGlobal: false,
            isInUse: [],
            _isNewOverwrite: true,
            _isFromCSV: true,
          };
          newVariables.push(newVar);
        }
      } else {
        // Create new variable
        const newVar: VariableEdit = {
          id: Date.now() + Math.random(),
          name: csvVar.name,
          value: csvVar.value || 0,
          currency: csvVar.currency,
          workspace_variable: scope.type === 'workspace',
          isGlobal: false,
          isInUse: [],
          alias: null,
          _isNew: true,
          _isFromCSV: true,
        };
        newVariables.push(newVar);
      }
    });

    setFieldValue('variables', newVariables);
  };

  const handleCSVRevert = (csvVariables: CsvVariable[]) => {
    if (!csvVariables.length || !preCSVVariables.length) return;
    setFieldValue('variables', _.cloneDeep(preCSVVariables));
    setPreCSVVariables([]);
  };

  useEffect(() => {
    const referenceVariables = preCSVVariables.length > 0 ? preCSVVariables : initialVariables;
    onDirtyChange?.(checkIsDirty(values.variables, referenceVariables));
  }, [values.variables, preCSVVariables, initialVariables]);

  return (
    <Div>
      <div className="h-padding">
        <div className="vw-head">
          {isModal ? (
            <Poppins className="m-title" px={28}>
              {scope.type === 'assessment' ? 'Assessment' : 'Workspace'} Variables
            </Poppins>
          ) : (
            <div />
          )}
          {isModal && (
            <div className="vw-head__checks">
              {scope.type === 'assessment' && (
                <div
                  className="vw-head__avo-check"
                  onClick={() => setShowAssessmentVarOnly(!showAssessmentVarOnly)}
                  data-cy="show-assessment-variables-only"
                >
                  <Poppins color="cflowerBlue" px={14} weight={500}>
                    Show assessment variables only
                  </Poppins>
                  <CheckBox small isChecked={showAssessmentVarOnly} />
                </div>
              )}
              <div
                className="vw-head__avo-check"
                onClick={() => setHideEmptyVariables(!hideEmptyVariables)}
                data-cy="hide-empty-variables"
              >
                <Poppins color="cflowerBlue" px={14} weight={500}>
                  Hide empty variables
                </Poppins>
                <CheckBox small isChecked={hideEmptyVariables} />
              </div>
            </div>
          )}
        </div>
      </div>
      <Spacer $px={20} />
      <div className="divider" />
      <Spacer $px={24} />

      {!isAssessmentVariablesLoading ? (
        <FormikProvider value={formik}>
          <motion.form
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.1 }}
            onSubmit={handleSubmit}
          >
            <div className="h-padding">
              <FieldArray name="variables">
                {({ remove, push }) => {
                  return (
                    <>
                      <div className="rows-grid">
                        <Head sort={sort} setSort={setSort} />
                        {values.variables.map((el, idx) => {
                          const hasWorkspaceVar = wVars?.some((wv) => wv.name === el.name);
                          const isVWar = el.workspace_variable || el.isGlobal || hasWorkspaceVar;
                          const isOverride =
                            scope.type === 'assessment' &&
                            hasWorkspaceVar &&
                            !el._isReverted &&
                            (assessmentVariables?.some((av) => av.name === el.name) || !!el._isNewOverwrite);
                          const canOverride = scope.type === 'assessment' && hasWorkspaceVar;
                          const shouldHideForAssessmentOnly = showAssessmentVarOnly && hasWorkspaceVar && !isOverride;
                          const isHidden = (hideEmptyVariables && el.value === null) || shouldHideForAssessmentOnly;
                          const typePrefix = el.workspace_variable ? 'w' : el.isGlobal ? 'g' : 'a';
                          const rowKey = `${typePrefix}-${el.id}`;

                          return (
                            <Row
                              key={rowKey}
                              idx={idx}
                              onRemove={() => {
                                if (isOverride && !el._isNew) {
                                  onOverride(el.name, false);
                                } else {
                                  remove(idx);
                                }
                              }}
                              scenarioNames={scenarioNames}
                              assessmentNames={assessmentNames}
                              scope={scope}
                              isVWar={isVWar}
                              canOverride={!!canOverride}
                              isOverride={!!isOverride}
                              onOverride={() => onOverride(el.name, !isOverride)}
                              isHidden={isHidden}
                              isGlobal={el.isGlobal}
                              existsInWorkspaceVars={!!hasWorkspaceVar}
                            />
                          );
                        })}
                      </div>
                      <Spacer $px={15} />
                      <VariableCSVReader
                        onApply={handleCSVApply}
                        onRevert={handleCSVRevert}
                        originalVariables={scope.type === 'assessment' ? assessmentVariables || [] : wVars}
                        scope={scope}
                      />
                      <Spacer $px={35} />
                      <div className="btn-container">
                        <Button
                          secondary
                          disabled={isSubmitting}
                          onClick={() => {
                            push(genEmptyVariable(scope.type));
                            mpEvent(MPEvents.ButtonClick, {
                              button: 'Add new variable',
                              modal: isModal ? 'Variables modal' : undefined,
                              tags: ['VARIABLES'],
                            });
                          }}
                          data-cy="add-variable-btn"
                        >
                          ADD NEW VARIABLE
                        </Button>
                        <Button
                          type="submit"
                          primary
                          disabled={!isValid || !checkIsDirty(values.variables, initialVariables) || isSubmitting}
                          data-cy="save-variables-btn"
                          onClick={() =>
                            mpEvent(MPEvents.ButtonClick, {
                              button: 'Save',
                              modal: isModal ? 'Variables modal' : undefined,
                              tags: ['VARIABLES'],
                            })
                          }
                        >
                          SAVE
                        </Button>
                      </div>
                    </>
                  );
                }}
              </FieldArray>
              {error && (
                <>
                  <Spacer $px={23} />
                  <div className="error">{error}</div>
                  <Spacer $px={23} />
                </>
              )}
            </div>
          </motion.form>
        </FormikProvider>
      ) : (
        <div className="h-padding">
          <TableSkeleton />
          <Spacer $px={54} />
        </div>
      )}
    </Div>
  );
};

export default WorkspaceVariablesForm;
